import {
  ActiveContact,
  AgentSnapshot,
  AgentStatusKind,
  AgentStatusOption,
  CallDirection,
  CallHistoryEntry,
  ContactAttributes,
  QueueSummary,
} from '../../../@types/connect.type';

// Mapping helpers between the Streams SDK's `connect.*` objects and our
// app-side snapshots. Kept as pure functions so they can be unit-tested
// without spinning up a fake CCP.
//
// The Streams SDK exposes its types via the global `connect` namespace
// declared by `amazon-connect-streams/src/index.d.ts`. We accept the SDK
// types directly here; nothing else in the codebase touches them.

const STATE_TYPE_MAP: Record<string, AgentStatusKind> = {
  routable: 'available',
  not_routable: 'busy',
  offline: 'offline',
  error: 'error',
  // System states the SDK reports during call legs:
  pending: 'busy',
  pending_busy: 'onCall',
  busy: 'onCall',
  after_call_work: 'afterCallWork',
};

const normaliseStateType = (raw: string | undefined | null): AgentStatusKind => {
  if (!raw) return 'default';
  return STATE_TYPE_MAP[raw.toLowerCase()] ?? 'default';
};

// Accepts either an `AgentState` (current state) or an `AgentStateDefinition`
// (one of the available states from the routing profile) — they overlap on
// the only fields we read.
export const buildStatusOption = (state: {
  name: string;
  type: connect.AgentStateType;
}): AgentStatusOption => ({
  name: state.name,
  kind: normaliseStateType(state.type as unknown as string),
  type: state.type as unknown as string,
});

export const buildAgentSnapshot = (agent: connect.Agent): AgentSnapshot => {
  const config = agent.getConfiguration();
  const current = agent.getState();
  const states = agent.getAgentStates();
  return {
    name: config?.name ?? null,
    status: {
      name: current?.name ?? 'Offline',
      kind: normaliseStateType(current?.type as unknown as string),
      type: (current?.type as unknown as string) ?? 'offline',
    },
    availableStates: states.map((s) => buildStatusOption(s)),
    statusChangedAt: current?.startTimestamp
      ? new Date(current.startTimestamp).toISOString()
      : new Date().toISOString(),
  };
};

const inferDirection = (contact: connect.Contact): CallDirection => {
  // The SDK exposes `getType()` for channel and `isInbound()` on connections.
  // For voice the initial connection is the customer leg; isInbound() returns
  // true for inbound calls.
  try {
    const initial = contact.getInitialConnection();
    return initial?.getEndpoint()?.endpointARN ? 'outbound' : 'inbound';
  } catch {
    return 'inbound';
  }
};

const extractPhoneNumber = (contact: connect.Contact): string | null => {
  try {
    const conn = contact.getInitialConnection();
    const endpoint = conn?.getEndpoint();
    const num = endpoint?.phoneNumber;
    if (!num || num === 'anonymous') return null;
    return num;
  } catch {
    return null;
  }
};

export const buildActiveContact = (
  contact: connect.Contact,
  state: ActiveContact['state']
): ActiveContact => ({
  contactId: contact.getContactId(),
  direction: inferDirection(contact),
  phoneNumber: extractPhoneNumber(contact),
  state,
  startedAt: new Date().toISOString(),
  connectedAt: state === 'connected' ? new Date().toISOString() : null,
  contactName: null,
});

export const buildHistoryEntry = (
  active: ActiveContact,
  outcome: CallHistoryEntry['outcome'],
  endedAt: string
): CallHistoryEntry => {
  const start = active.connectedAt ?? active.startedAt;
  const durationSeconds = active.connectedAt
    ? Math.max(0, Math.round((new Date(endedAt).getTime() - new Date(start).getTime()) / 1000))
    : null;
  return {
    id: active.contactId,
    direction: active.direction,
    phoneNumber: active.phoneNumber,
    contactName: active.contactName,
    startedAt: active.startedAt,
    durationSeconds,
    outcome,
  };
};

// Streams returns attributes as `{ key: { name, value } | undefined }`. The
// rest of the app only ever wants the value, so flatten to a plain map and
// drop undefined entries.
export const flattenAttributes = (
  raw: connect.AttributeDictionary | undefined
): ContactAttributes => {
  if (!raw) return {};
  const out: ContactAttributes = {};
  for (const key of Object.keys(raw)) {
    const entry = raw[key];
    if (entry && typeof entry.value === 'string') {
      out[key] = entry.value;
    }
  }
  return out;
};

export const extractQueueName = (contact: connect.Contact): string | null => {
  try {
    return contact.getQueue()?.name ?? null;
  } catch {
    return null;
  }
};

export const buildQueueSummaries = (agent: connect.Agent): QueueSummary[] => {
  try {
    const profile = agent.getRoutingProfile();
    const queues = profile?.queues ?? [];
    // Some routing profile payloads return `queueId` but not `queueARN`. The
    // ARN is preferred (used as a stable React key + identifier) but we
    // fall back to queueId so legitimate queues aren't dropped.
    return queues
      .filter((q) => q.name && (q.queueARN || q.queueId))
      .map((q) => ({
        queueArn: q.queueARN ?? q.queueId,
        name: q.name,
        // BFF endpoint required to populate these — see Backlog.
        pendingCount: null,
        agentsOnline: null,
      }));
  } catch {
    return [];
  }
};
